import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { NotFound, RouteWithSubRoutes, useFeatures, useSession } from '@cordata/buffet';
import { PageLayout } from '~/components/common';

import { AdminProvider } from '~/utils/admin';

const Admin = ({ routes }) => {
  const [loading, setLoading] = useState(true);
  const [listItems, setListItems] = useState([]);
  const [activeItems, setActiveItems] = useState([]);
  const [hasOrgAdmin, setHasOrgAdmin] = useState(false);
  const { path, url } = useRouteMatch();
  const {
    hasProviderAdmin,
    hasFacilityAdmin,
    hasInsuranceCarrierAdmin,
    hasClinicalTrialsAdmin,
    hasDepartmentAdmin,
    hasReminderGroupAdmin,
    hasAppIdentity,
    hasAppDivisionAdmin,
    hasAppKickstarter,
    hasUserAdmin,
    hasDataAdmin,
  } = useFeatures();
  const { current } = useSession();
  const destinations = JSON.parse(localStorage.getItem('destinations'));
  const navWebURL = destinations?.find(({ name }) => name === 'Navigator Community')?.url;

  useEffect(() => {
    if (current) {
      setHasOrgAdmin(current.attributes
        .accessibleOrgs.some(o =>
          o.permissions.filter(p =>
            p.securityPermissionName === 'hasAppAdmin')));
    }
  }, [current]);

  useEffect(() => {
    setLoading(true);
    const sideNavItems = [{
      label: 'users',
      to: '/admin/users',
      isActive: hasAppKickstarter || hasOrgAdmin || hasUserAdmin,
    }, {
      label: 'providers',
      to: '/admin/providers',
      isActive: hasAppKickstarter || hasOrgAdmin || hasProviderAdmin,
    }, {
      label: 'facilities',
      to: '/admin/facilities',
      isActive: hasAppKickstarter || hasOrgAdmin || hasFacilityAdmin,
    }, {
      label: 'insurance-carriers',
      to: '/admin/insurance-carriers',
      isActive: hasAppKickstarter || hasOrgAdmin || hasInsuranceCarrierAdmin,
    }, {
      label: 'clinical-trials',
      to: '/admin/clinical-trials',
      isActive: hasAppKickstarter || hasOrgAdmin || hasClinicalTrialsAdmin,
    }, {
      label: 'reminder-groups',
      to: '/admin/reminder-groups',
      isActive: hasAppKickstarter || hasOrgAdmin || hasReminderGroupAdmin,
    }, {
      label: 'departments',
      to: '/admin/departments',
      isActive: hasDepartmentAdmin || hasAppKickstarter || (hasOrgAdmin && !hasAppIdentity),
    }, {
      label: 'match-actions',
      to: '/admin/identity-matches/match-actions',
      isActive: hasAppIdentity && hasDataAdmin,
    }, {
      label: 'match-lists',
      to: '/admin/identity-matches/match-lists',
      isActive: hasAppIdentity && hasDataAdmin,
    }, {
      label: 'Match Action Log',
      href: `${navWebURL}/reports/configured/Match_Action_Log`,
      isActive: hasAppIdentity && hasDataAdmin,
    }, {
      label: 'Outbound Emails',
      to: '/admin/outbound-emails',
      isActive: hasAppKickstarter || hasOrgAdmin || hasAppDivisionAdmin,
    }, {
      label: 'Sendgrid Events',
      to: '/admin/sendgrid-events',
      isActive: hasAppKickstarter,
    }];

    setActiveItems(sideNavItems.filter(item => item.isActive));
    setListItems(sideNavItems);
    setLoading(false);
  }, [
    hasProviderAdmin,
    hasFacilityAdmin,
    hasInsuranceCarrierAdmin,
    hasClinicalTrialsAdmin,
    hasReminderGroupAdmin,
    hasDepartmentAdmin,
    hasAppDivisionAdmin,
    hasAppIdentity,
    hasUserAdmin,
    hasDataAdmin,
    hasOrgAdmin,
    hasAppKickstarter,
  ]);

  const firstItemTo = activeItems && activeItems[0] ? activeItems[0].to : '/default-path';

  if (activeItems.length === 0) {
    return <div>Loading...</div>;
  }

  return (
    <PageLayout
      sideNavItems={!loading && listItems}
    >
      <AdminProvider>
        <Switch>
          <Route
            exact
            path={url}
            render={() => <Redirect to={firstItemTo} />}
          />

          {routes.map(r => (
            <RouteWithSubRoutes key={r.path} {...r} path={`${path}/${r.path}`} />
          ))}

          <Route path="*"><NotFound isRoute /></Route>
        </Switch>
      </AdminProvider>
    </PageLayout>
  );
};

Admin.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.shape({
    path: PropTypes.string.isRequired,
  })).isRequired,
};

export default Admin;
